import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Bell, 
  BellOff,
  Shield,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle2,
  Trash2,
  Eye,
  EyeOff,
  Mail,
  Key,
  Globe,
  Loader2,
  X,
  Settings,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationCenter({ isOpen, onClose }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('alerts');
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      fetchAlerts();
      fetchPreferences();
    }
  }, [isOpen, user]);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/notifications/alerts`);
      setAlerts(response.data.alerts);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API}/notifications/preferences`);
      setPreferences(response.data);
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    }
  };

  const updatePreference = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    
    try {
      await axios.put(`${API}/notifications/preferences`, {
        breach_alerts: newPrefs.breach_alerts,
        password_alerts: newPrefs.password_alerts,
        weekly_digest: newPrefs.weekly_digest,
        dark_web_alerts: newPrefs.dark_web_alerts
      });
      toast.success('Preferences updated');
    } catch (error) {
      toast.error('Failed to update preferences');
      setPreferences(preferences);
    }
  };

  const markAsRead = async (alertId) => {
    try {
      await axios.post(`${API}/notifications/alerts/${alertId}/read`);
      setAlerts(alerts.map(a => a.id === alertId ? { ...a, read: true } : a));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/alerts/read-all`);
      setAlerts(alerts.map(a => ({ ...a, read: true })));
      setUnreadCount(0);
      toast.success('All alerts marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/notifications/alerts/${alertId}`);
      setAlerts(alerts.filter(a => a.id !== alertId));
      toast.success('Alert deleted');
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const simulateBreach = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/notifications/simulate-breach`);
      toast.success('Simulated breach alert created!');
      fetchAlerts();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Premium subscription required');
      } else {
        toast.error('Failed to simulate breach');
      }
    } finally {
      setLoading(false);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'high':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      case 'medium':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'low':
        return <Info className="w-5 h-5 text-blue-500" />;
      default:
        return <Info className="w-5 h-5 text-gray-500" />;
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      info: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return colors[severity] || colors.info;
  };

  const getAlertTypeIcon = (type) => {
    switch (type) {
      case 'password_breach':
        return <Key className="w-4 h-4" />;
      case 'email_breach':
        return <Mail className="w-4 h-4" />;
      case 'dark_web':
        return <Globe className="w-4 h-4" />;
      default:
        return <Shield className="w-4 h-4" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" 
      data-testid="notification-center"
      onClick={(e) => {
        // Close when clicking on backdrop (not on modal content)
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="w-full max-w-2xl max-h-[80vh] bg-[#0a0a0a] border border-[#262626] rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#262626]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center">
              <Bell className="w-5 h-5 text-[#00FF94]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Breach Alerts</h2>
              <p className="text-xs text-gray-500">
                {unreadCount > 0 ? `${unreadCount} unread alert${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#171717] rounded-lg transition-colors"
            data-testid="notification-close-btn"
          >
            <X className="w-5 h-5 text-gray-400 hover:text-white" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#262626]">
          <button
            onClick={() => setActiveTab('alerts')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'alerts'
                ? 'text-[#00FF94] border-b-2 border-[#00FF94]'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Bell className="w-4 h-4 inline mr-2" />
            Alerts
            {unreadCount > 0 && (
              <Badge className="ml-2 bg-red-500/20 text-red-400 border-red-500/30 text-xs">
                {unreadCount}
              </Badge>
            )}
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'settings'
                ? 'text-[#00FF94] border-b-2 border-[#00FF94]'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Settings
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {activeTab === 'alerts' ? (
            <div className="space-y-4">
              {/* Actions */}
              <div className="flex items-center justify-between">
                <Button
                  onClick={markAllAsRead}
                  variant="ghost"
                  size="sm"
                  disabled={unreadCount === 0}
                  className="text-gray-400 hover:text-white"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Mark all read
                </Button>
                
                {user?.is_premium && (
                  <Button
                    onClick={simulateBreach}
                    variant="outline"
                    size="sm"
                    disabled={loading}
                    className="border-[#262626] text-gray-400 hover:text-white hover:border-[#00FF94]/30"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 mr-2" />
                    )}
                    Simulate Alert
                  </Button>
                )}
              </div>

              {/* Alerts List */}
              <ScrollArea className="h-[400px]">
                {alerts.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-[#171717] border border-[#262626] flex items-center justify-center mb-4">
                      <BellOff className="w-8 h-8 text-gray-600" />
                    </div>
                    <p className="text-gray-500">No alerts yet</p>
                    <p className="text-xs text-gray-600 mt-1">
                      You'll be notified when we detect breaches
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alerts.map((alert) => (
                      <div
                        key={alert.id}
                        className={`p-4 rounded-lg border transition-colors ${
                          alert.read
                            ? 'bg-[#0a0a0a] border-[#262626]'
                            : 'bg-[#171717] border-[#00FF94]/20'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-0.5">
                            {getSeverityIcon(alert.severity)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className={`font-medium text-sm ${alert.read ? 'text-gray-400' : 'text-white'}`}>
                                {alert.title}
                              </h4>
                              <Badge className={`text-xs ${getSeverityBadge(alert.severity)}`}>
                                {alert.severity}
                              </Badge>
                            </div>
                            <p className="text-xs text-gray-500 mb-2">{alert.message}</p>
                            <div className="flex items-center gap-3 text-xs text-gray-600">
                              <span className="flex items-center gap-1">
                                {getAlertTypeIcon(alert.alert_type)}
                                {alert.alert_type.replace('_', ' ')}
                              </span>
                              <span>
                                {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {!alert.read && (
                              <button
                                onClick={() => markAsRead(alert.id)}
                                className="p-1.5 hover:bg-[#262626] rounded transition-colors"
                                title="Mark as read"
                              >
                                <Eye className="w-4 h-4 text-gray-500 hover:text-[#00FF94]" />
                              </button>
                            )}
                            <button
                              onClick={() => deleteAlert(alert.id)}
                              className="p-1.5 hover:bg-[#262626] rounded transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-gray-500 hover:text-red-400" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          ) : (
            /* Settings Tab */
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Customize which alerts you want to receive
              </p>
              
              {preferences && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 bg-[#171717] rounded-lg border border-[#262626]">
                    <div className="flex items-center gap-3">
                      <Key className="w-5 h-5 text-[#00FF94]" />
                      <div>
                        <p className="text-sm font-medium text-white">Password Breach Alerts</p>
                        <p className="text-xs text-gray-500">Get notified when checked passwords are found in breaches</p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.password_alerts}
                      onCheckedChange={(checked) => updatePreference('password_alerts', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-[#171717] rounded-lg border border-[#262626]">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-[#00FF94]" />
                      <div>
                        <p className="text-sm font-medium text-white">Email Breach Alerts</p>
                        <p className="text-xs text-gray-500">Get notified about email breaches and exposures</p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.breach_alerts}
                      onCheckedChange={(checked) => updatePreference('breach_alerts', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-[#171717] rounded-lg border border-[#262626]">
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-[#00FF94]" />
                      <div>
                        <p className="text-sm font-medium text-white">Dark Web Alerts</p>
                        <p className="text-xs text-gray-500">Get notified when your data appears on the dark web</p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.dark_web_alerts}
                      onCheckedChange={(checked) => updatePreference('dark_web_alerts', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-[#171717] rounded-lg border border-[#262626]">
                    <div className="flex items-center gap-3">
                      <Bell className="w-5 h-5 text-[#00FF94]" />
                      <div>
                        <p className="text-sm font-medium text-white">Weekly Security Digest</p>
                        <p className="text-xs text-gray-500">Receive a weekly summary of your privacy status</p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.weekly_digest}
                      onCheckedChange={(checked) => updatePreference('weekly_digest', checked)}
                    />
                  </div>
                </div>
              )}

              {!user?.is_premium && (
                <div className="p-4 bg-[#00FF94]/5 border border-[#00FF94]/20 rounded-lg">
                  <p className="text-sm text-[#00FF94] font-medium mb-1">
                    Upgrade to Premium
                  </p>
                  <p className="text-xs text-gray-500">
                    Get real-time email notifications when breaches are detected
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
